// MODIFICADO - Solo usar token de Keycloak via apiService
import apiService from './apiService';
import { appConfig } from '../config/keycloak';
// COMENTADO - Solo usar token de Keycloak, no AuthService para client_credentials
// import authService from './AuthService.ts';

class LocationService {
  constructor() {
    // Usar la configuración centralizada de appConfig
    this.baseURL = `${appConfig.baseUrl}/param-service/api/params`;
  }

  /**
   * Get the base URL for the current environment
   * @returns {string} Base URL for the current environment
   */
  getBaseURL() {
    return this.baseURL;
  }

  /**
   * Make a direct fetch request without authentication
   * @param {string} endpoint - API endpoint
   * @returns {Promise<Array>} Response data
   */
  async makeRequest(endpoint) {
    try {
      // MODIFICADO - Usar token de Keycloak via apiService
      let token = await apiService.getToken();
      if (!token) {
        console.warn('No authentication token available for location request');
      }

      const doFetch = async () => fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
      });

      let response = await doFetch();


      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data || [];
    } catch (error) {
      console.error('Location API Error:', error);
      return [];
    }
  }

  /**
   * Search countries by query
   * @param {string} query - Search term for country name
   * @returns {Promise<Array>} Array of countries
   */
  async searchCountries(query) {
    const endpoint = `${this.baseURL}/countries?q=${encodeURIComponent(query)}`;
    return this.makeRequest(endpoint);
  }

  /**
   * Search states by country and query
   * @param {string} countryId - Google Places country ID
   * @param {string} query - Search term for state name
   * @returns {Promise<Array>} Array of states
   */
  async searchStates(countryId, query) {
    const endpoint = `${this.baseURL}/states?countryId=${encodeURIComponent(countryId)}&q=${encodeURIComponent(query)}`;
    return this.makeRequest(endpoint);
  }

  /**
   * Search cities by country, state and query
   * @param {string} countryId - Google Places country ID
   * @param {string} stateId - Google Places state ID
   * @param {string} query - Search term for city name
   * @returns {Promise<Array>} Array of cities
   */
  async searchCities(countryId, stateId, query) {
    const endpoint = `${this.baseURL}/cities?countryId=${encodeURIComponent(countryId)}&stateId=${encodeURIComponent(stateId)}&q=${encodeURIComponent(query)}`;
    return this.makeRequest(endpoint);
  }

  /**
   * Get dashboard embed URL
   * @param {string} dashboardName - Name of the dashboard to embed
   * @returns {Promise<Object>} Object containing embedUrl and other dashboard data
   */
  async getDashboardEmbedUrl(dashboardName) {
    try {
      let token = await apiService.getToken();
      if (!token) {
        console.warn('No token available for dashboard request');
        throw new Error('Authentication token not available');
      }

      const baseUrl = `${appConfig.baseUrl}/param-service/api/dashboards/embed`;
      const endpoint = `${baseUrl}?dashboardName=${encodeURIComponent(dashboardName)}`;

      const doFetch = async () => fetch(endpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
      });

      let response = await doFetch();

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data || !data.embedUrl) {
        throw new Error('Invalid response from dashboard service: missing embedUrl');
      }

      return {
        embedUrl: data.embedUrl,
        status: data.status
      };
    } catch (error) {
      console.error('Dashboard Embed URL Error:', error);
      throw error;
    }
  }

  /**
   * Load holidays from backend for a date range
   * @param {string} startDate - Start date in YYYY-MM-DD format
   * @param {string} endDate - End date in YYYY-MM-DD format
   * @returns {Promise<Array>} Array of holiday dates
   */
  async loadHolidays(startDate, endDate) {
    const endpoint = `${this.baseURL}/holidays/dates?start=${encodeURIComponent(startDate)}&end=${encodeURIComponent(endDate)}`;
    return this.makeRequest(endpoint);
  }

  /**
   * Get next business day for a given date
   * @param {string} date - Date in YYYY-MM-DD format
   * @returns {Promise<string>} Next business day in YYYY-MM-DD format
   */
  async getNextBusinessDay(date) {
    const endpoint = `${this.baseURL}/holidays/next-business-day?date=${encodeURIComponent(date)}`;
    return this.makeRequest(endpoint);
  }
}

// Create and export a singleton instance
const locationService = new LocationService();
export default locationService;